import { useSelector } from 'react-redux';
import { get } from 'lodash';
import { ApplicantEntity } from '@lendi/core-constants';
import { Body } from '@lendi-ui/typography';

import { APPLICANT_TYPE_OPTIONS, FIELD_PATHS } from '@funnels/constants';
import { useFormValues } from '@funnels/hooks';
import { ReduxStoreData, StoreApplicant } from '@funnels/types';
import { FunnelGrid, FunnelGridUnit } from '@funnels/ui-components/_common';
import { HelpTooltip } from '@funnels/ui-components/Common';
import { RadioButtonsField } from '@funnels/ui-components/Fields';
import { initialFormValues } from '@funnels/utils';

interface ApplicantTypeProps {
  applicantIndex: number;
  onApplicantTypeChange?: (value: string) => void;
}

export const ApplicantType = ({ applicantIndex, onApplicantTypeChange }: ApplicantTypeProps) => {
  const [formValues, setFormValue] = useFormValues();
  const selectedEntity = get(formValues, FIELD_PATHS.APPLICANT_ENTITY(applicantIndex));
  const tooltipContent =
    'Servicing will still need to be completed manually if Company or Trust is selected. Workshop your deal will be uplifted soon to support this scenario.';
  // using store applicant value to check whether this applicant is created or not,
  // so that we can determine whether or not to disable the applicant type selection.
  const storeApplicants: StoreApplicant[] = useSelector((state: ReduxStoreData) => state.applicants.data);

  const unsetData = (selectedValue: ApplicantEntity) => {
    const customerId = get(formValues, FIELD_PATHS.APPLICANT_CUSTOMER_ID(applicantIndex));
    const applicantType = get(formValues, FIELD_PATHS.APPLICANT_TYPE(applicantIndex));

    const applicantId = get(formValues, FIELD_PATHS.APPLICANT_ID(applicantIndex));
    const applicantClientId = get(formValues, FIELD_PATHS.APPLICANT_CLIENT_ID(applicantIndex));
    const properties = {
      id: applicantId,
      clientId: applicantClientId,
      entity: selectedValue,
      applicantType,
      ...(selectedValue !== ApplicantEntity.Person && { contact: undefined, dependants: undefined }),
    };

    setFormValue(
      FIELD_PATHS.APPLICANT(applicantIndex),
      initialFormValues.INITIAL_APPLICANT({ id: customerId }, properties)
    );
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedValue = e.target.value;

    if (onApplicantTypeChange) {
      onApplicantTypeChange(selectedValue);
    }

    unsetData(selectedValue as ApplicantEntity);
  };

  return (
    <FunnelGrid direction="column">
      <FunnelGridUnit mb="sm">
        <Body>
          Applicant type
          <HelpTooltip position="right" content={tooltipContent} />
        </Body>
      </FunnelGridUnit>
      <FunnelGridUnit mb="md">
        <RadioButtonsField
          name={FIELD_PATHS.APPLICANT_ENTITY(applicantIndex)}
          isDisabled={!!storeApplicants[applicantIndex]?.id}
          radios={APPLICANT_TYPE_OPTIONS}
          direction="row"
          selectedValue={selectedEntity}
          onChange={handleChange}
        />
      </FunnelGridUnit>
    </FunnelGrid>
  );
};
